/** Scroll controls shared by Snooze product and collection carousels. */
class SnoozeCarousel extends HTMLElement {
  static observedAttributes = ['data-layout'];

  connectedCallback() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.initialize());
  }

  initialize() {
    if (!this.isConnected) return;
    this.controller?.abort();
    this.observer?.disconnect();
    this.itemsObserver?.disconnect();
    this.track = this.querySelector('[data-carousel-track]');
    this.controls = this.querySelector('[data-carousel-controls]');
    if (!this.track || !this.controls) return;
    this.previous = this.controls.querySelector('[data-carousel-direction="previous"]');
    this.next = this.controls.querySelector('[data-carousel-direction="next"]');
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.controls.addEventListener('click', (event) => {
      const button = event.target.closest('[data-carousel-direction]');
      if (button && !button.disabled) this.move(button === this.next ? 1 : -1);
    }, { signal });
    this.track.addEventListener('scroll', () => this.update(), { signal, passive: true });
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.contains(this)) this.initialize();
    }, { signal });
    this.track.addEventListener('keydown', (event) => {
      if (event.target !== this.track || this.dataset.layout !== 'carousel') return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.move((event.key === 'ArrowRight') !== rtl ? 1 : -1);
    }, { signal });
    this.observer = new ResizeObserver(() => this.update());
    this.observer.observe(this.track);
    this.itemsObserver = new MutationObserver(() => this.update());
    this.itemsObserver.observe(this.track, { childList: true });
    this.update();
  }

  attributeChangedCallback() {
    this.update();
  }

  move(direction) {
    const first = this.track.firstElementChild;
    if (!first) return;
    const style = getComputedStyle(this.track);
    const distance = first.getBoundingClientRect().width + (Number.parseFloat(style.columnGap) || 0);
    this.track.scrollBy({
      left: direction * distance * (style.direction === 'rtl' ? -1 : 1),
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  }

  update() {
    if (!this.track || !this.controls) return;
    const maximum = this.track.scrollWidth - this.track.clientWidth;
    const active = this.dataset.layout === 'carousel' && this.track.clientWidth > 0 && maximum > 1;
    this.controls.hidden = !active;
    const position = Math.abs(this.track.scrollLeft);
    if (this.previous) this.previous.disabled = !active || position <= 1;
    if (this.next) this.next.disabled = !active || position >= maximum - 1;
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.controller?.abort();
    this.observer?.disconnect();
    this.itemsObserver?.disconnect();
  }
}

if (!customElements.get('snooze-carousel')) customElements.define('snooze-carousel', SnoozeCarousel);
